import { Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import type { StringKeyOf } from 'type-fest';

import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { TrackingEventType } from 'src/app/core/services/tracking/models/tracking-event-type.enum';
import { TrackingInput } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { TrackingPagename } from 'src/app/core/services/tracking/models/tracking-pagename.enum';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { EffectsBase } from 'src/app/store/effects-base';
import { RouterActionType } from 'src/app/store/router/router.actions';
import { selectFilteredPreviousRouteState, selectRouteState } from 'src/app/store/router/router.selectors';
import { filterChanged, filterTruthy } from 'src/app/utils/functions/rx-filters';

const ROUTE_MAPPING_TRACKING = {
  isMotorization: TrackingPagename.Motorization,
  isVehicleLength: TrackingPagename.VehicleLength,
  isPreconfig: TrackingPagename.Preconfig
};

const isKeyPartOfActivatedRouteState = (
  key: StringKeyOf<ActivatedRouteState>,
  route: ActivatedRouteState | undefined
): boolean => (route ? key in route && !!route[key] : false);

@Injectable()
export class MotorizationTrackingEffects extends EffectsBase {
  // DO NOT REMOVE, even though it might appear as unused in your IDE
  // also tracks vehicleLength and preconfig pages
  public trackingPageChange$: Observable<void> = createEffect(
    (): Observable<void> =>
      this.actions$.pipe(
        ofType(RouterActionType.RouterNavigationEnd),
        mergeMap(() =>
          combineLatest([
            this.store.select(selectRouteState).pipe(filterTruthy(), filterChanged(), first()),
            this.store.pipe(selectFilteredPreviousRouteState, first())
          ])
        ),
        map(([activatedRoute, previousRoute]) => {
          if (activatedRoute) {
            const routeState = this.getRouteState(activatedRoute, previousRoute);
            if (routeState) {
              const trackingInput: TrackingInput = {
                event: TrackingEventType.PageView,
                routeState
              };
              this.trackingService.sendTrackingData(trackingInput);
            }
          }
        })
      ),
    { dispatch: false }
  );

  public constructor(
    private readonly trackingService: TrackingService,
    private readonly store: Store
  ) {
    super();
  }

  private getRouteState(
    activatedRoute: ActivatedRouteState | undefined,
    previousRoute: ActivatedRouteState | undefined
  ): TrackingPagename | undefined {
    for (const key in ROUTE_MAPPING_TRACKING) {
      if (
        isKeyPartOfActivatedRouteState(key as StringKeyOf<ActivatedRouteState>, activatedRoute) &&
        !isKeyPartOfActivatedRouteState(key as StringKeyOf<ActivatedRouteState>, previousRoute)
      ) {
        return ROUTE_MAPPING_TRACKING[key as keyof typeof ROUTE_MAPPING_TRACKING];
      }
    }
    return undefined;
  }
}
